import type { Lang } from '../i18n'

export type MeditationCopy = {
  title: string
  headerLabel: string
  open: string
  footerLink: string
  kicker: string
  introTitle: string
  introText: string
  introNote: string
  introNoteTouch: string
  startSound: string
  startSilent: string
  soundOn: string
  soundOff: string
  volume: string
  breathe: string
  breatheShort: string
  breatheHint: string
  inhale: string
  hold: string
  exhale: string
  timer: string
  timerShort: string
  timerLeft: string
  timerMenu: string
  noTimer: string
  minutes: string
  endTitle: string
  endText: string
  stay: string
  back: string
  close: string
  escHint: string
  controls: string
  audioUnavailable: string
}

export const meditationCopy: Record<Lang, MeditationCopy> = {
  ru: {
    title: 'Медитация',
    headerLabel: 'Медитация',
    open: 'Медитация: космос и спокойная музыка',
    footerLink: 'Медитация в космосе',
    kicker: 'Пауза для себя',
    introTitle: 'Устройтесь поудобнее',
    introText: 'Лучше в наушниках 🎧',
    introNote: 'Просто смотрите на звёзды и дышите. Выйти можно в любой момент — кнопкой ✕ или клавишей Esc.',
    introNoteTouch: 'Просто смотрите на звёзды и дышите. Выйти можно в любой момент — кнопкой ✕.',
    startSound: 'Начать со звуком',
    startSilent: 'Без звука',
    soundOn: 'Включить звук',
    soundOff: 'Выключить звук',
    volume: 'Громкость',
    breathe: 'Дыхание',
    breatheShort: 'Дыхание',
    breatheHint: 'Ритм дыхания: вдох 4 секунды, пауза 2, выдох 6',
    inhale: 'Вдох',
    hold: 'Пауза',
    exhale: 'Выдох',
    timer: 'Таймер',
    timerShort: 'Таймер',
    timerLeft: 'Таймер, осталось {time}',
    timerMenu: 'Длительность сеанса',
    noTimer: 'Без таймера',
    minutes: 'мин',
    endTitle: 'Хорошего дня',
    endText: 'Спасибо, что побыли здесь. Возвращайтесь, когда захочется тишины.',
    stay: 'Побыть ещё',
    back: 'Вернуться на сайт',
    close: 'Закрыть медитацию',
    escHint: 'Esc — выход',
    controls: 'Управление медитацией',
    audioUnavailable: 'В этом браузере звук недоступен',
  },
  en: {
    title: 'Meditation',
    headerLabel: 'Meditate',
    open: 'Meditate: space and calm music',
    footerLink: 'Meditation in space',
    kicker: 'A moment for yourself',
    introTitle: 'Make yourself comfortable',
    introText: 'Best with headphones 🎧',
    introNote: 'Just watch the stars and breathe. You can leave anytime with ✕ or Esc.',
    introNoteTouch: 'Just watch the stars and breathe. You can leave anytime with ✕.',
    startSound: 'Start with sound',
    startSilent: 'Start in silence',
    soundOn: 'Turn sound on',
    soundOff: 'Turn sound off',
    volume: 'Volume',
    breathe: 'Breathing',
    breatheShort: 'Breathe',
    breatheHint: 'Breathing rhythm: in for 4 seconds, hold for 2, out for 6',
    inhale: 'Breathe in',
    hold: 'Hold',
    exhale: 'Breathe out',
    timer: 'Timer',
    timerShort: 'Timer',
    timerLeft: 'Timer, {time} left',
    timerMenu: 'Session length',
    noTimer: 'No timer',
    minutes: 'min',
    endTitle: 'Have a good day',
    endText: 'Thank you for taking this moment. Come back whenever you need some quiet.',
    stay: 'Stay a little longer',
    back: 'Back to the website',
    close: 'Leave meditation',
    escHint: 'Esc to exit',
    controls: 'Meditation controls',
    audioUnavailable: 'Sound is not available in this browser',
  },
  de: {
    title: 'Meditation',
    headerLabel: 'Meditation',
    open: 'Meditation: Weltraum und ruhige Musik',
    footerLink: 'Meditation im All',
    kicker: 'Eine kleine Auszeit',
    introTitle: 'Machen Sie es sich bequem',
    introText: 'Am schönsten mit Kopfhörern 🎧',
    introNote: 'Schauen Sie einfach den Sternen zu und atmen Sie ruhig. Mit ✕ oder Esc können Sie die Meditation jederzeit beenden.',
    introNoteTouch: 'Schauen Sie einfach den Sternen zu und atmen Sie ruhig. Mit ✕ können Sie die Meditation jederzeit beenden.',
    startSound: 'Mit Ton starten',
    startSilent: 'Ohne Ton',
    soundOn: 'Ton einschalten',
    soundOff: 'Ton ausschalten',
    volume: 'Lautstärke',
    breathe: 'Atmung',
    breatheShort: 'Atmen',
    breatheHint: 'Atemrhythmus: 4 Sekunden einatmen, 2 halten, 6 ausatmen',
    inhale: 'Einatmen',
    hold: 'Halten',
    exhale: 'Ausatmen',
    timer: 'Timer',
    timerShort: 'Timer',
    timerLeft: 'Timer, Restzeit {time}',
    timerMenu: 'Dauer der Sitzung',
    noTimer: 'Ohne Timer',
    minutes: 'Min.',
    endTitle: 'Einen schönen Tag noch',
    endText: 'Danke, dass Sie sich diesen Moment genommen haben. Kommen Sie wieder, wann immer Sie Ruhe brauchen.',
    stay: 'Noch etwas bleiben',
    back: 'Zurück zur Website',
    close: 'Meditation schließen',
    escHint: 'Esc zum Beenden',
    controls: 'Steuerung der Meditation',
    audioUnavailable: 'In diesem Browser ist kein Ton verfügbar',
  },
  es: {
    title: 'Meditación',
    headerLabel: 'Meditar',
    open: 'Meditar: espacio y música tranquila',
    footerLink: 'Meditación en el espacio',
    kicker: 'Una pausa para ti',
    introTitle: 'Acomódate',
    introText: 'Mejor con auriculares 🎧',
    introNote: 'Solo mira las estrellas y respira. Puedes salir cuando quieras con ✕ o Esc.',
    introNoteTouch: 'Solo mira las estrellas y respira. Puedes salir cuando quieras con ✕.',
    startSound: 'Empezar con sonido',
    startSilent: 'Sin sonido',
    soundOn: 'Activar el sonido',
    soundOff: 'Silenciar',
    volume: 'Volumen',
    breathe: 'Respiración',
    breatheShort: 'Respirar',
    breatheHint: 'Ritmo de respiración: inhala 4 segundos, sostén 2, exhala 6',
    inhale: 'Inhala',
    hold: 'Sostén',
    exhale: 'Exhala',
    timer: 'Temporizador',
    timerShort: 'Tiempo',
    timerLeft: 'Temporizador, quedan {time}',
    timerMenu: 'Duración de la sesión',
    noTimer: 'Sin temporizador',
    minutes: 'min',
    endTitle: 'Que tengas un buen día',
    endText: 'Gracias por regalarte este momento. Vuelve cuando necesites un poco de calma.',
    stay: 'Quedarme un poco más',
    back: 'Volver al sitio',
    close: 'Cerrar la meditación',
    escHint: 'Esc para salir',
    controls: 'Controles de la meditación',
    audioUnavailable: 'El sonido no está disponible en este navegador',
  },
  zh: {
    title: '冥想',
    headerLabel: '冥想',
    open: '冥想：星空与舒缓音乐',
    footerLink: '太空冥想',
    kicker: '给自己一点时间',
    introTitle: '找个舒服的姿势',
    introText: '建议戴上耳机 🎧',
    introNote: '只需看着星空，慢慢呼吸。随时可点 ✕ 或按 Esc 退出。',
    introNoteTouch: '只需看着星空，慢慢呼吸。随时可点 ✕ 退出。',
    startSound: '伴随音乐开始',
    startSilent: '静音开始',
    soundOn: '开启声音',
    soundOff: '关闭声音',
    volume: '音量',
    breathe: '呼吸',
    breatheShort: '呼吸',
    breatheHint: '呼吸节奏：吸气 4 秒，屏息 2 秒，呼气 6 秒',
    inhale: '吸气',
    hold: '屏息',
    exhale: '呼气',
    timer: '定时',
    timerShort: '定时',
    timerLeft: '定时，剩余时间：{time}',
    timerMenu: '冥想时长',
    noTimer: '不定时',
    minutes: '分钟',
    endTitle: '祝您今天愉快',
    endText: '感谢您给自己留出这段时间。想要安静时，欢迎随时回来。',
    stay: '再待一会儿',
    back: '返回网站',
    close: '关闭冥想',
    escHint: '按 Esc 退出',
    controls: '冥想控制',
    audioUnavailable: '此浏览器无法播放声音',
  },
  th: {
    title: 'ทำสมาธิ',
    headerLabel: 'ทำสมาธิ',
    open: 'ทำสมาธิ: อวกาศและดนตรีผ่อนคลาย',
    footerLink: 'ทำสมาธิกลางอวกาศ',
    kicker: 'เวลาพักสำหรับตัวคุณเอง',
    introTitle: 'นั่งให้สบาย',
    introText: 'ใส่หูฟังจะดีที่สุด 🎧',
    introNote: 'เพียงมองดวงดาวแล้วหายใจช้า ๆ ออกได้ทุกเมื่อด้วยปุ่ม ✕ หรือ Esc',
    introNoteTouch: 'เพียงมองดวงดาวแล้วหายใจช้า ๆ ออกได้ทุกเมื่อด้วยปุ่ม ✕',
    startSound: 'เริ่มพร้อมเสียง',
    startSilent: 'เริ่มแบบไม่มีเสียง',
    soundOn: 'เปิดเสียง',
    soundOff: 'ปิดเสียง',
    volume: 'ระดับเสียง',
    breathe: 'การหายใจ',
    breatheShort: 'หายใจ',
    breatheHint: 'จังหวะหายใจ: หายใจเข้า 4 วินาที กลั้นไว้ 2 วินาที หายใจออก 6 วินาที',
    inhale: 'หายใจเข้า',
    hold: 'กลั้นไว้',
    exhale: 'หายใจออก',
    timer: 'ตั้งเวลา',
    timerShort: 'ตั้งเวลา',
    timerLeft: 'ตั้งเวลา เหลือเวลา {time}',
    timerMenu: 'ระยะเวลาการทำสมาธิ',
    noTimer: 'ไม่ตั้งเวลา',
    minutes: 'นาที',
    endTitle: 'ขอให้เป็นวันที่ดี',
    endText: 'ขอบคุณที่ให้เวลากับตัวเอง กลับมาได้ทุกเมื่อที่อยากพบความสงบ',
    stay: 'อยู่ต่ออีกสักพัก',
    back: 'กลับสู่เว็บไซต์',
    close: 'ปิดโหมดทำสมาธิ',
    escHint: 'กด Esc เพื่อออก',
    controls: 'ปุ่มควบคุมการทำสมาธิ',
    audioUnavailable: 'เบราว์เซอร์นี้เล่นเสียงไม่ได้',
  },
}

export const getMeditationCopy = (lang: Lang) => meditationCopy[lang]
